import * as React from "react";
import { Upload, Link2, X, AlertCircle } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { uploadImageToSupabase, validateImageUrl, validateImageFile } from "@/utils/imageUpload";

export interface ImageUploadProps {
  value?: string;
  onChange: (imageUrl: string) => void;
  placeholder?: string;
  maxSize?: number;
  acceptedFormats?: string[];
  className?: string;
  disabled?: boolean;
}

const ImageUpload = React.forwardRef<HTMLDivElement, ImageUploadProps>(
  ({
    value,
    onChange,
    placeholder = "Enter image URL or upload file",
    maxSize = 5 * 1024 * 1024, // 5MB
    acceptedFormats = ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    className,
    disabled = false,
    ...props
  }, ref) => {
    const [urlInput, setUrlInput] = React.useState(value || '');
    const [isUploading, setIsUploading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [inputMode, setInputMode] = React.useState<'url' | 'file'>('url');
    
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // Update internal state when value prop changes
    React.useEffect(() => {
      setUrlInput(value || '');
      setError(null);
    }, [value]);

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const url = e.target.value;
      setUrlInput(url);
      setError(null);
      
      if (url) {
        // Validate URL format
        const validation = validateImageUrl(url);
        if (!validation.isValid) {
          setError(validation.error || 'Invalid URL');
          return;
        }
        
        onChange(url);
      } else {
        onChange('');
      }
    };

    const handleFileUpload = async (file: File) => {
      setIsUploading(true);
      setError(null);

      try {
        // Validate file
        const validation = validateImageFile(file);
        if (!validation.isValid) {
          setError(validation.error || 'Invalid file');
          return;
        }

        // Upload to Supabase
        const result = await uploadImageToSupabase(file);
        
        // Update state
        setUrlInput(result.url);
        onChange(result.url);
        setInputMode('url'); // Switch back to URL mode to show the uploaded URL
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Upload failed';
        setError(errorMessage);
        console.error('Upload error:', err);
      } finally {
        setIsUploading(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileUpload(file);
      }
    };

    const handleFileButtonClick = () => {
      fileInputRef.current?.click();
    };

    const handleClear = () => {
      setUrlInput('');
      setError(null);
      onChange('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    };

    return (
      <div ref={ref} className={cn("space-y-3", className)} {...props}>
        {/* Input Section */}
        <div className="flex gap-2">
          <div className="flex-1">
            {inputMode === 'url' ? (
              <div className="relative">
                <Input
                  type="url"
                  value={urlInput}
                  onChange={handleUrlChange}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  disabled={disabled || isUploading}
                  className={cn(
                    "pr-20",
                    error && "border-destructive focus-visible:ring-destructive"
                  )}
                />
                {urlInput && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClear}
                    disabled={disabled || isUploading}
                    className="absolute right-1 top-1 h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center border-2 border-dashed border-input rounded-md p-4 text-sm text-muted-foreground">
                {isUploading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                    <span>Uploading...</span>
                  </div>
                ) : (
                  <span>Click "Upload File" to select an image</span>
                )}
              </div>
            )}
          </div>
          
          {/* Toggle Buttons */}
          <div className="flex gap-1">
            <Button
              type="button"
              variant={inputMode === 'url' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setInputMode('url')}
              disabled={disabled || isUploading}
              className="px-3"
            >
              <Link2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant={inputMode === 'file' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setInputMode('file');
                handleFileButtonClick();
              }}
              disabled={disabled || isUploading}
              className="px-3"
            >
              <Upload className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats.join(',')}
          onChange={handleFileInputChange}
          disabled={disabled || isUploading}
          className="hidden"
        />

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}

        {/* Help Text */}
        <p className="text-xs text-muted-foreground">
          Supported formats: {acceptedFormats.join(', ').toUpperCase()} • Max size: {Math.round(maxSize / (1024 * 1024))}MB
        </p>
      </div>
    );
  }
);

ImageUpload.displayName = "ImageUpload";

export { ImageUpload }; 